import { BudgetAllocation } from '../types/projects';

/**
 * Pure helpers for working with a project's budget allocation (the three
 * category amounts). No scalar total is ever stored — the total is always the
 * sum of the three buckets, computed here.
 */

/**
 * Sum of the three buckets (the project's total budget). 0 when unset.
 * Defensive against malformed input (e.g. a legacy scalar budget or a partial
 * object): non-object input and non-finite buckets coerce to 0 rather than NaN.
 */
export const sumAllocation = (allocation: BudgetAllocation | null | undefined): number => {
    if (!allocation || typeof allocation !== 'object') return 0;
    return (
        (Number(allocation.investmentAmount) || 0) +
        (Number(allocation.buildingMaintenanceAmount) || 0) +
        (Number(allocation.propertyTechMaintenanceAmount) || 0)
    );
};

/** True when there is no budget to speak of (unset, malformed, or all buckets 0). */
export const isAllocationEmpty = (allocation: BudgetAllocation | null | undefined): boolean => {
    return sumAllocation(allocation) === 0;
};

/**
 * Per-year share of each bucket when a budget is distributed equally across the
 * inclusive calendar-year span [startYear, endYear]. A reversed or single-point
 * span collapses to one year (never produces NaN/Infinity/negative shares).
 */
export const distributeAllocationOverYears = (
    allocation: BudgetAllocation,
    startYear: number,
    endYear: number
): BudgetAllocation => {
    const years = Math.max(1, endYear - startYear + 1);
    return {
        investmentAmount: allocation.investmentAmount / years,
        buildingMaintenanceAmount: allocation.buildingMaintenanceAmount / years,
        propertyTechMaintenanceAmount: allocation.propertyTechMaintenanceAmount / years
    };
};

export type ParseAllocationResult =
    | { ok: true; allocation: BudgetAllocation | null } // null = cleared (all blank/zero)
    | { ok: false; error: string };

/** Parse one raw input field; blank → 0, invalid (negative/non-finite) → null. */
const parseAmount = (raw: string): number | null => {
    const trimmed = raw.trim();
    if (trimmed === '') return 0;
    const value = Number(trimmed);
    if (!Number.isFinite(value) || value < 0) return null;
    return value;
};

/**
 * Parse the three editor inputs into a BudgetAllocation.
 * - All blank/zero → { ok: true, allocation: null } (clear the budget).
 * - Any negative or non-numeric value → { ok: false, error }.
 */
export const parseAllocationInput = (
    investmentRaw: string,
    buildingMaintenanceRaw: string,
    propertyTechMaintenanceRaw: string
): ParseAllocationResult => {
    const investmentAmount = parseAmount(investmentRaw);
    const buildingMaintenanceAmount = parseAmount(buildingMaintenanceRaw);
    const propertyTechMaintenanceAmount = parseAmount(propertyTechMaintenanceRaw);

    if (
        investmentAmount === null ||
        buildingMaintenanceAmount === null ||
        propertyTechMaintenanceAmount === null
    ) {
        return { ok: false, error: 'Budgetfördelning måste vara positiva tal' };
    }

    const allocation: BudgetAllocation = {
        investmentAmount,
        buildingMaintenanceAmount,
        propertyTechMaintenanceAmount
    };

    if (isAllocationEmpty(allocation)) {
        return { ok: true, allocation: null };
    }
    return { ok: true, allocation };
};
